/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/defaultVATSettings.scss';

import {
    AwesomeIcon,
    ChangeFunction,
    ChangeKeyFunction,
    CloseButton,
    ElementPosition,
    ErrorType,
    Loader,
    WithBackground
} from '@dvrd/dvr-controls';

import React, {MouseEventHandler, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {BolmateSelectItem, VATType, VATValue} from '../../../../util/interfaces';

import {CountryVATValues, translateVatValue, VATValuesMatch} from '../../../../util/vatUtil';

import {BolmateButton, BolmateNumberInput, BolmateSelect} from '../../../controls';

import {getCountryLabel, getVatItemsByCountry} from '../../../../util/countryUtil';

import classNames from 'classnames';

interface Props {
    onClose: MouseEventHandler;
    onAddCountry: ChangeFunction;
    onDeleteCountry: (countryCode: string) => MouseEventHandler;
    onClickChange: (countryCode: string, value: VATValue) => MouseEventHandler;
    onChange: ChangeKeyFunction<string, VATValue | number | string>;
    onSubmit: MouseEventHandler;
    countryValues: CountryVATValues;
    loading: boolean;
    active: boolean;
    errors: Record<string, ErrorType>;
    availableCountries: Array<[string, string]>;
}

function isCustomVAT(value: VATValue | null, options: Array<VATValue>): boolean {
    if (!value || ![VATType.DEFAULT, VATType.CUSTOM].includes(value.vat_type)) return false;
    if (value.vat_type === VATType.CUSTOM) return true;
    return !options.find((option: VATValue) => VATValuesMatch(value, option));
}

export default function DefaultVATSettings(props: Props) {
    const {t} = useTranslation();
    const {
        onClose,
        onAddCountry,
        onDeleteCountry,
        onClickChange,
        onChange,
        onSubmit,
        countryValues,
        loading,
        active,
        errors,
        availableCountries
    } = props;
    const countryItems: Array<BolmateSelectItem> = useMemo(() => {
        const items: Array<BolmateSelectItem> = [
            {value: '', label: t('settings.vat.chooseCountry', 'Kies een land'), selectable: false}
        ];
        return items.concat(availableCountries.map((entry: [string, string]) => ({value: entry[0], label: entry[1]})));
    }, [availableCountries]);

    function renderBody() {
        return (
            <div className='body-container'>
                <Loader active={loading} />
                <label className='body-title'>
                    {t('settings.vat.setDefaultRates', 'Standaard BTW tarieven instellen')}
                </label>
                <div className='countries'>{countryValues.sortedCountryCodes.map(renderCountry)}</div>
                <div className='add-country'>
                    <label>{t('settings.vat.addDefaultFor', 'Standaard BTW tarief toevoegen voor:')}</label>
                    <BolmateSelect
                        items={countryItems}
                        onChange={onAddCountry}
                        value=''
                        itemsHeight='20rem'
                        itemsPosition='top'
                    />
                </div>
            </div>
        );
    }

    function renderCountry(countryCode: string, index: number) {
        const options = getVatItemsByCountry(countryCode, undefined, false);
        const currentValue = countryValues.getCountryValue(countryCode);
        const isCustom = isCustomVAT(currentValue, options);
        const hasError = !!errors[countryCode];
        return (
            <div key={index} className='country-default-vat'>
                {!!errors[countryCode] && <label className='error-label'>{errors[countryCode]}</label>}
                <div className='vat-row'>
                    <label className={classNames('country-label', hasError && 'error')}>
                        {getCountryLabel(countryCode)}
                    </label>
                    <div className='vat-options'>{options.map(renderCountryValue(countryCode))}</div>
                    <AwesomeIcon name='trash-alt' onClick={onDeleteCountry(countryCode)} className='btn-delete' />
                </div>
                {isCustom && (
                    <BolmateNumberInput
                        onChange={onChange(countryCode)}
                        value={currentValue!.value}
                        label={t('settings.vat.customRate', 'Aangepast tarief')}
                        className='custom-vat-field'
                        ornaments={{element: <span>%</span>, placement: ElementPosition.RIGHT}}
                        asNumber
                        wholeNumbers
                        disableSymbols
                        autoFocus
                        autoSelect
                    />
                )}
            </div>
        );
    }

    function renderCountryValue(countryCode: string) {
        return function (value: VATValue, index: number, options: Array<VATValue>) {
            const countryValue = countryValues.getCountryValue(countryCode);
            let active: boolean;
            if (isCustomVAT(countryValue, options)) active = value.vat_type === VATType.CUSTOM;
            else active = VATValuesMatch(countryValue, value);
            return (
                <div
                    key={index}
                    className={classNames('vat-value', active && 'active')}
                    onClick={onClickChange(countryCode, value)}
                >
                    <label>{translateVatValue(value)}</label>
                </div>
            );
        };
    }

    return (
        <WithBackground active={active} onClose={onClose} preventBackgroundClick>
            <div className='default-popup default-vat-settings'>
                <div className='header-container'>
                    <label className='header-title'>{t('settings.vat.defaultRates', 'Standaard BTW tarieven')}</label>
                    <CloseButton onClick={onClose} />
                </div>
                {renderBody()}
                <div className='footer-container btn-2'>
                    <BolmateButton
                        onClick={onClose}
                        label={t('settings.vat.cancel', 'Annuleren')}
                        disabled={loading}
                        secondary
                    />
                    <BolmateButton onClick={onSubmit} label={t('settings.vat.save', 'Opslaan')} disabled={loading} />
                </div>
            </div>
        </WithBackground>
    );
}
